// ==========================================
// TYPES & INTERFACES
// ==========================================

import type { DepartmentType, GeoLevel } from "../types/database";


export interface StateItem {
  id: string;
  name: string;
}

export interface CreateStatePayload {
  name: string;
}

export interface UpdateStatePayload {
  name?: string;
}

export interface DistrictItem {
  id: string;
  name: string;
  stateId: string;
  state?: StateItem;
}

export interface CreateDistrictPayload {
  name: string;
  stateId: string;
}

export interface UpdateDistrictPayload {
  name?: string;
  stateId?: string;
}

export interface TownshipItem {
  id: string;
  name: string;
  districtId: string;
  district?: DistrictItem;
}

export interface CreateTownshipPayload {
  name: string;
  districtId: string;
}

export interface UpdateTownshipPayload {
  name?: string;
  districtId?: string;
}

export interface OfficeItem {
  id: string;
  code: string;
  name: string;
  phone: string;
  level: GeoLevel;
  department: DepartmentType;
  stateId?: string | null;
  districtId?: string | null;
  townshipId?: string | null;
  parentOfficeId?: string | null;
  state?: StateItem | null;
  district?: DistrictItem | null;
  township?: TownshipItem | null;
  parentOffice?: OfficeItem | null;
  childOffices?: OfficeItem[];
  createdAt?: string;
  updatedAt?: string;
}

export interface PermissionItem {
  id: string;
  name: string;
  description: string;
}

export interface CreateOfficePayload {
  code: string;
  name: string;
  phone: string;
  level: GeoLevel;
  department: DepartmentType;
  stateId?: string;
  districtId?: string;
  townshipId?: string;
  parentOfficeId?: string;
}

export type UpdateOfficePayload = Partial<CreateOfficePayload>;

export interface InitialSettingsData {
  states: StateItem[];
  districts: DistrictItem[];
  townships: TownshipItem[];
  offices: OfficeItem[];
  permissions: PermissionItem[];
}


export interface NotificationItem {
  id: string;
  userId: string;
  title: string;
  message: string;
  formType: string;
  documentId?: string;
  isRead: boolean;
  createdAt: string;
}

export interface NotificationResponse {
  items: NotificationItem[];
  unreadCount: number;
}

export interface UnreadCountResponse {
  unreadCount: number;
}

export interface MarkReadResponse {
  count: number; // Prisma updateMany returns { count: number }
}


export interface ApiResponse<T> {
  data: T;
}

// ==========================================
// HTTP HELPER
// ==========================================

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.yourdomain.gov.mm/v1';

async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('access_token');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.message || response.statusText;
    throw new Error(`API Error (${response.status}): ${errorMessage}`);
  }

  // Handle 204 No Content responses (like DELETE)
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// ==========================================
// INITIAL SETTINGS
// ==========================================

export async function getSettings(): Promise<ApiResponse<InitialSettingsData>> {
  return apiFetch<ApiResponse<InitialSettingsData>>('/settings/init');
}

// ==========================================
// STATES CRUD
// ==========================================

export async function getStates(): Promise<ApiResponse<StateItem[]>> {
  return apiFetch<ApiResponse<StateItem[]>>('/states');
}

export async function getStateById(id: string): Promise<ApiResponse<StateItem>> {
  return apiFetch<ApiResponse<StateItem>>(`/states/${id}`);
}

export async function createState(payload: CreateStatePayload): Promise<ApiResponse<StateItem>> {
  return apiFetch<ApiResponse<StateItem>>('/states', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateState(id: string, payload: UpdateStatePayload): Promise<ApiResponse<StateItem>> {
  return apiFetch<ApiResponse<StateItem>>(`/states/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deleteState(id: string): Promise<void> {
  await apiFetch<void>(`/states/${id}`, { method: 'DELETE' });
}

// ==========================================
// DISTRICTS CRUD
// ==========================================

export async function getDistricts(stateId?: string): Promise<ApiResponse<DistrictItem[]>> {
  const query = stateId ? `?stateId=${encodeURIComponent(stateId)}` : '';
  return apiFetch<ApiResponse<DistrictItem[]>>(`/districts${query}`);
}

export async function getDistrictById(id: string): Promise<ApiResponse<DistrictItem>> {
  return apiFetch<ApiResponse<DistrictItem>>(`/districts/${id}`);
}

export async function createDistrict(payload: CreateDistrictPayload): Promise<ApiResponse<DistrictItem>> {
  return apiFetch<ApiResponse<DistrictItem>>('/districts', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateDistrict(id: string, payload: UpdateDistrictPayload): Promise<ApiResponse<DistrictItem>> {
  return apiFetch<ApiResponse<DistrictItem>>(`/districts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deleteDistrict(id: string): Promise<void> {
  await apiFetch<void>(`/districts/${id}`, { method: 'DELETE' });
}

// ==========================================
// TOWNSHIPS CRUD
// ==========================================

export async function getTownships(districtId?: string): Promise<ApiResponse<TownshipItem[]>> {
  const query = districtId ? `?districtId=${encodeURIComponent(districtId)}` : '';
  return apiFetch<ApiResponse<TownshipItem[]>>(`/townships${query}`);
}

export async function getTownshipById(id: string): Promise<ApiResponse<TownshipItem>> {
  return apiFetch<ApiResponse<TownshipItem>>(`/townships/${id}`);
}

export async function createTownship(payload: CreateTownshipPayload): Promise<ApiResponse<TownshipItem>> {
  return apiFetch<ApiResponse<TownshipItem>>('/townships', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateTownship(id: string, payload: UpdateTownshipPayload): Promise<ApiResponse<TownshipItem>> {
  return apiFetch<ApiResponse<TownshipItem>>(`/townships/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deleteTownship(id: string): Promise<void> {
  await apiFetch<void>(`/townships/${id}`, { method: 'DELETE' });
}

// ==========================================
// OFFICES CRUD
// ==========================================

export async function getOffices(): Promise<ApiResponse<OfficeItem[]>> {
  return apiFetch<ApiResponse<OfficeItem[]>>('/offices');
}

export async function getOfficeById(id: string): Promise<ApiResponse<OfficeItem>> {
  return apiFetch<ApiResponse<OfficeItem>>(`/offices/${id}`);
}

export async function createOffice(payload: CreateOfficePayload): Promise<ApiResponse<OfficeItem>> {
  return apiFetch<ApiResponse<OfficeItem>>('/offices', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateOffice(id: string, payload: UpdateOfficePayload): Promise<ApiResponse<OfficeItem>> {
  return apiFetch<ApiResponse<OfficeItem>>(`/offices/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deleteOffice(id: string): Promise<void> {
  await apiFetch<void>(`/offices/${id}`, { method: 'DELETE' });
}


export async function getNotificationList(): Promise<ApiResponse<NotificationResponse>> {
  return apiFetch<ApiResponse<NotificationResponse>>('/notification-list');
}

// 2. Fetch unread count only
export async function getUnreadCount(): Promise<ApiResponse<UnreadCountResponse>> {
  return apiFetch<ApiResponse<UnreadCountResponse>>('/unread-count');
}

// 3. Mark all as read
export async function markAllAsRead(): Promise<ApiResponse<MarkReadResponse>> {
  return apiFetch<ApiResponse<MarkReadResponse>>('/mark-all-read', {
    method: 'PATCH',
  });
}

export async function markOneAsRead(documentId: string): Promise<ApiResponse<MarkReadResponse>> {
  return apiFetch<ApiResponse<MarkReadResponse>>('/mark-one-read', {
    method: 'PATCH',
    body: JSON.stringify({ documentId }),
  });
}
